import React, { useState, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { 
  CheckCircleIcon, 
  ExclamationTriangleIcon, 
  InformationCircleIcon, 
  XCircleIcon,
  CheckIcon,
  TrashIcon
} from '@heroicons/react/24/outline'
import { Notification } from '../../types/notification'
import { formatDistanceToNow } from 'date-fns'

interface NotificationPanelProps {
  notifications: Notification[]
  onMarkAsRead: (id: string, source?: 'in_app' | 'announcement') => void
  onMarkAllAsRead: () => void
  onDelete: (id: string, source?: 'in_app' | 'announcement') => void
  onClearAll: () => void
  isOpen: boolean
  onClose: () => void
}

const iconMap = {
  success: CheckCircleIcon,
  error: XCircleIcon,
  warning: ExclamationTriangleIcon,
  info: InformationCircleIcon,
}

const colorMap = {
  success: {
    bg: 'bg-success-50',
    border: 'border-success-200',
    text: 'text-success-800',
    icon: 'text-success-400',
  },
  error: {
    bg: 'bg-danger-50',
    border: 'border-danger-200',
    text: 'text-danger-800',
    icon: 'text-danger-400',
  },
  warning: {
    bg: 'bg-warning-50',
    border: 'border-warning-200',
    text: 'text-warning-800',
    icon: 'text-warning-400',
  },
  info: {
    bg: 'bg-info-50',
    border: 'border-info-200',
    text: 'text-info-800',
    icon: 'text-info-400',
  },
}

export const NotificationPanel: React.FC<NotificationPanelProps> = ({
  notifications,
  onMarkAsRead,
  onMarkAllAsRead,
  onDelete,
  onClearAll,
  isOpen,
  onClose,
}) => {
  const [activeTab, setActiveTab] = useState<'all' | 'unread'>('all')

  const unreadCount = useMemo(() => notifications.filter(n => !n.read).length, [notifications])
  const filteredNotifications = useMemo(
    () => activeTab === 'unread' ? notifications.filter(n => !n.read) : notifications,
    [notifications, activeTab]
  )

  if (!isOpen) return null

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-25 z-40"
        onClick={onClose}
      />
      
      {/* Panel */}
              <div className="fixed top-16 right-4 w-96 bg-white dark:bg-navy-800/70 rounded-lg shadow-xl border border-neutral-200 dark:border-navy-700/50 z-50 max-h-[calc(100vh-5rem)] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-neutral-200">
          <h3 className="text-lg font-semibold text-neutral-900">
            Notifications
          </h3>
          <div className="flex items-center space-x-2">
            {unreadCount > 0 && (
              <button
                onClick={onMarkAllAsRead}
                className="text-sm text-primary-600 hover:text-primary-700"
              >
                Mark all read
              </button>
            )}
            {notifications.length > 0 && (
              <button
                onClick={onClearAll}
                className="text-sm text-neutral-500 hover:text-neutral-700"
              >
                Clear all
              </button>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-neutral-200">
          <button
            onClick={() => setActiveTab('all')}
            className={`flex-1 px-4 py-2 text-sm font-medium ${
              activeTab === 'all'
                ? 'text-primary-600 border-b-2 border-primary-600'
                : 'text-neutral-500 hover:text-neutral-700'
            }`}
          >
            All ({notifications.length})
          </button>
          <button
            onClick={() => setActiveTab('unread')}
            className={`flex-1 px-4 py-2 text-sm font-medium ${
              activeTab === 'unread'
                ? 'text-primary-600 border-b-2 border-primary-600'
                : 'text-neutral-500 hover:text-neutral-700'
            }`}
          >
            Unread ({unreadCount})
          </button>
        </div>

        {/* Notifications List */}
        <div className="flex-1 overflow-y-auto">
          {filteredNotifications.length === 0 ? (
            <div className="p-8 text-center">
              <InformationCircleIcon className="mx-auto h-12 w-12 text-neutral-400" />
              <h3 className="mt-2 text-sm font-medium text-neutral-900">
                No notifications
              </h3>
              <p className="mt-1 text-sm text-neutral-500">
                {activeTab === 'unread' 
                  ? 'You\'re all caught up!' 
                  : 'You don\'t have any notifications yet.'
                }
              </p>
            </div>
          ) : (
            <div className="divide-y divide-neutral-200">
              {filteredNotifications.map((notification) => {
                const IconComponent = iconMap[notification.type]
                const colors = colorMap[notification.type]
                
                return (
                  <div
                    key={notification.id}
                    className={`p-4 hover:bg-neutral-50 transition-colors ${
                      !notification.read ? 'bg-blue-50' : ''
                    }`}
                  >
                    <div className="flex items-start space-x-3">
                      <div className="flex-shrink-0">
                        <IconComponent className={`h-5 w-5 ${colors.icon}`} />
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between">
                          <p className={`text-sm font-medium ${colors.text}`}>
                            {notification.title}
                          </p>
                          <div className="flex items-center space-x-1 ml-2">
                            {!notification.read && (
                              <button
                                onClick={() => onMarkAsRead(notification.id, notification.source)}
                                className="p-1 text-neutral-400 hover:text-neutral-600"
                                title="Mark as read"
                              >
                                <CheckIcon className="h-4 w-4" />
                              </button>
                            )}
                            <button
                              onClick={() => onDelete(notification.id, notification.source)}
                              className="p-1 text-neutral-400 hover:text-neutral-600"
                              title="Delete"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                        
                        <p className={`mt-1 text-sm ${colors.text} opacity-80`}>
                          {notification.message}
                        </p>
                        
                        <div className="mt-2 flex items-center justify-between">
                          <p className="text-xs text-neutral-500">
                            {formatDistanceToNow(new Date(notification.createdAt), { addSuffix: true })}
                          </p>
                          
                          {notification.actionUrl && (
                            <Link
                              to={notification.actionUrl}
                              className="text-xs text-primary-600 hover:text-primary-700 font-medium"
                              onClick={onClose}
                            >
                              View details
                            </Link>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </div>
    </>
  )
}
